import dayjs from '../../utils/dayjs';
import { Button, DatePicker, InlineStack, IndexFilters, IndexFiltersProps, Link, useSetIndexFiltersMode, Text, Select } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';

import helpers from '../../helpers';
import { useDeleteUserMeta, useGetUserMeta, useUpdateUserMeta } from 'queries/user_meta.query';
import useDidMountEffect from 'components/useDidMountEffect';
import __ from 'languages/index';
import { formatDateRange } from 'components/filter.helper';
import QuickSearchUser from 'components/quickSearchUser';
import QuickSearchVehicle from 'components/quickSearchVehicle';

import { TypedUser } from 'interface/user.model';

export default function TransactionFilter({ onCallback, loading }: { onCallback: (output: any) => void; loading: boolean }) {
  const { mutateAsync: getEntity, isPending: _loading } = useGetUserMeta();
  const { mutateAsync: updateEntity, isPending: updating } = useUpdateUserMeta();
  const { mutateAsync: deleteEntity } = useDeleteUserMeta();

  const key_for_saving_setting = `tab_transaction_index_`;

  /**
   * Load data
   */

  const getItem = useCallback(async (meta_key: string) => {
    meta_key = helpers.parseMetaKey(meta_key);
    return await getEntity(meta_key);
  }, []);

  const removeItem = useCallback(async (meta_key: string) => {
    meta_key = helpers.parseMetaKey(meta_key);
    return await deleteEntity(meta_key);
  }, []);

  const updateItem = useCallback(async (meta_key: string, meta_value: any) => {
    meta_key = helpers.parseMetaKey(meta_key);
    return await updateEntity({
      meta_key: meta_key,
      meta_value: meta_value,
    });
  }, []);

  /**
   * Load data
   */

  const rootSearchPath = window.location.search;
  const mainQuery: any = useMemo(() => helpers.ExtractUrl(rootSearchPath) || {}, [rootSearchPath]);

  const { query, casher, driver_id, vehicle_id, sort, createdAt, payment_method } = mainQuery;
  const orderDepartureDate = mainQuery['order_data.departure_date'];
  const [sortSelected, setSortSelected] = useState<string[]>([helpers.colonToOldSort(sort) || 'createdAt, desc']);
  const [queryValue, setQueryValue] = useState(query || '');
  const [casherID, setCasherID] = useState(casher || '');
  const [casherName, setCasherName] = useState(null);
  const [driverID, setDriverID] = useState(driver_id || '');
  const [driverName, setDriverName] = useState(null);
  const [vehicleID, setVehicleID] = useState(vehicle_id || '');
  const [vehicleName, setVehicleName] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(payment_method || '');
  const [selected, setSelected] = useState(0); // tab selected
  const [dateRanges, setDateRanges] = useState<any>(formatDateRange(createdAt));
  const [departureDateRanges, setDepartureDateRanges] = useState<any>(formatDateRange(orderDepartureDate));
  /**
   * Change tab, load new data from url down to filter
   */
  const reloadDataDownFromURL = (__: any) => {
    const { query, createdAt, payment_method, casher, driver_id, vehicle_id } = __;
    setCasherID(casher || '');
    setDriverID(driver_id || '');
    setQueryValue(query || '');
    setVehicleID(vehicle_id || '');
    setPaymentMethod(payment_method || '');
    setDateRanges(formatDateRange(createdAt));
    setDepartureDateRanges(formatDateRange(__['order_data.departure_date']));
  };

  /** Load mặc định phát đầu tiên từ URL */
  useEffect(() => {
    reloadDataDownFromURL(mainQuery);
  }, [mainQuery]);

  /***************************************************************************
/***************************************************************************
/***************************************************************************
 * FILTERs
 */

  function getCurrentSetting(): string {
    const rootSearchPath = window.location.search;
    let mainQuery: any = helpers.ExtractUrl(rootSearchPath) || false;
    return JSON.stringify(mainQuery);
  }

  /**
   * reduce loading,
   */

  const onChangeMainQueryCallback = useMemo(() => {
    return helpers.debounce((_value) => {
      onCallback?.call(this, _value);
    }, 500);
  }, []);

  useDidMountEffect(() => {
    if (!sortSelected) return;
    let sortString = '';
    if (Array.isArray(sortSelected)) {
      sortString = sortSelected.join('');
    } else {
      sortString = sortSelected;
    }
    onChangeMainQueryCallback({ sort: helpers.oldSortToColon(sortString) });
  }, [sortSelected]);

  /**
   * Hold array of all tab
   */
  const [itemStrings, setItemStrings] = useState(['ALL']);

  useEffect(() => {
    getItem('customer_tabs')
      .then((response) => {
        if (helpers.isEmpty(response)) return;
        setItemStrings(response || []);
      })
      .catch((e) => console.log(e, 'e_8d'));
  }, []);

  // save name to
  useDidMountEffect(() => {
    let tabsToString = JSON.stringify(itemStrings);
    updateItem('customer_tabs', tabsToString);
  }, [itemStrings]);

  /**
   * click change tab ...
   * @param index
   * @returns
   */

  const changeTabFilterAction = useCallback(async (index: number, name: string) => {
    if (index === 0) {
      reloadDataDownFromURL({});
      return onChangeMainQueryCallback(false);
    }
    onChangeMainQueryCallback(false);
    await helpers.sleep(100);
    getItem(key_for_saving_setting + name)
      .then((response: any) => {
        if (!response) return;
        onChangeMainQueryCallback(response);
        reloadDataDownFromURL(response);
      })
      .catch((_) => {});
  }, []);

  const deleteView = (index: number, value: string) => {
    const newItemStrings = [...itemStrings];
    newItemStrings.splice(index, 1);
    setItemStrings(newItemStrings);
    setSelected(0);
    removeItem(key_for_saving_setting + value);
  };

  const duplicateView = async (name: string) => {
    setItemStrings([...itemStrings, name]);
    setSelected(itemStrings.length);
    // clone setting
    updateItem(key_for_saving_setting + name, getCurrentSetting());
    return true;
  };

  const tabs: any[] =
    Array.isArray(itemStrings) &&
    itemStrings?.map((item, index) => ({
      content: item,
      index,
      onAction: () => {
        changeTabFilterAction(index, item);
      },
      id: `${item}-${index}`,
      isLocked: index === 0,
      actions:
        index === 0
          ? []
          : [
              {
                type: 'rename',
                onAction: () => {},
                onPrimaryAction: async (value: string): Promise<boolean> => {
                  const newItemsStrings = tabs?.map((item, idx) => {
                    if (idx === index) {
                      return value;
                    }
                    return item.content;
                  });
                  setItemStrings(newItemsStrings);
                  return true;
                },
              },
              {
                type: 'duplicate',
                onPrimaryAction: async (value: string): Promise<boolean> => {
                  duplicateView(value);
                  return true;
                },
              },
              {
                type: 'delete',
                onPrimaryAction: async (value: string) => {
                  deleteView(index, value);
                  return true;
                },
              },
            ],
    }));

  const { mode, setMode } = useSetIndexFiltersMode();
  const handleFiltersQueryChange = useCallback((value: string) => {
    setQueryValue(value);
    onChangeMainQueryCallback({ query: value, page: 1 });
  }, []);

  /**
   * Xoa hoac huy form search ...
   */
  const handleQueryValueRemove = useCallback(() => {
    setQueryValue('');
    onChangeMainQueryCallback({ query: '', page: 1 });
  }, []);

  const sortOptions: any[] = [
    { label: 'Ngày tạo', value: 'createdAt, asc', directionLabel: __('ascending') },
    { label: 'Ngày tạo', value: 'createdAt, desc', directionLabel: __('descending') },
    { label: 'Số tiền', value: 'amount, asc', directionLabel: __('ascending') },
    { label: 'Số tiền', value: 'amount, desc', directionLabel: __('descending') },
  ];

  function disambiguateLabel(key: string, value: string | any): string {
    switch (key) {
      case 'keyword':
        return `Từ khóa: ${value}`;
      case 'casher':
        return `Người thu tiền: ${casherName || 'Tất cả'}`;

      case 'paymentMethod':
        return `Loại thanh toán: /${value || 'Tất cả'}`;
      case 'driver':
        return `Lái xe: ${driverName || 'Tất cả'}`;
      case 'vehicle':
        return `Xe: ${vehicleName || 'Tất cả'}`;

      case 'daterange':
        let from = value[0] ? dayjs(new Date(Number(value[0]))).format('DD-MM-YYYY') : '-';
        let to = value[1] ? dayjs(new Date(Number(value[1]))).format('DD-MM-YYYY') : '-';
        return `Ngày thanh toán: từ ${from} đến: ${to}`;
      case 'departureDateRange':
        let departureFrom = value[0] ? dayjs(new Date(Number(value[0]))).format('DD-MM-YYYY') : '-';
        let departureTo = value[1] ? dayjs(new Date(Number(value[1]))).format('DD-MM-YYYY') : '-';
        return `Ngày đi: từ ${departureFrom} đến: ${departureTo}`;
      default:
        return value as string;
    }
  }

  const handlePaymentTypeWithChange = useCallback((value: string) => {
    setPaymentMethod(value);
    onChangeMainQueryCallback({ payment_method: value, page: 1 });
  }, []);

  const handleCasherWithChange = useCallback((value: string) => {
    setCasherID(value);
    onChangeMainQueryCallback({ casher: value, page: 1 });
  }, []);

  const handlePaymentTypedWithRemove = useCallback(() => {
    setPaymentMethod('');
    onChangeMainQueryCallback({ payment_method: '', page: 1 });
  }, []);

  const handleCasherWithRemove = useCallback(() => {
    setCasherID('');
    setCasherName(null);
    onChangeMainQueryCallback({ casher: '', page: 1 });
  }, []);

  const handleDriverWithChange = useCallback((value: string) => {
    setDriverID(value);
    onChangeMainQueryCallback({ driver_id: value, page: 1 });
  }, []);

  const handleDriverWithRemove = useCallback(() => {
    setDriverID('');
    setDriverName(null);
    onChangeMainQueryCallback({ driver_id: '', page: 1 });
  }, []);

  const handleVehicleWithChange = useCallback((value: string) => {
    setVehicleID(value);
    onChangeMainQueryCallback({ vehicle_id: value, page: 1 });
  }, []);

  const handleVehicleWithRemove = useCallback(() => {
    setVehicleID('');
    setVehicleName(null);
    onChangeMainQueryCallback({ vehicle_id: '', page: 1 });
  }, []);

  const handCalendarRemove = useCallback(() => {
    setDateRanges([]);
    onChangeMainQueryCallback({ createdAt: '', page: 1 });
  }, []);

  const handleDepartureCalendarRemove = useCallback(() => {
    setDepartureDateRanges([]);
    onChangeMainQueryCallback({ 'order_data.departure_date': '', page: 1 });
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    handleQueryValueRemove();
    handlePaymentTypedWithRemove();
    handCalendarRemove();
    handleDepartureCalendarRemove();
    handleCasherWithRemove();
    handleDriverWithRemove();
    handleVehicleWithRemove();

    /** Phải để cái này ở cuối để nó reset hết toàn bộ bộ lọc về vị trí ban đầu */
    onChangeMainQueryCallback(false);
  }, [
    handleQueryValueRemove,
    handlePaymentTypedWithRemove,
    handCalendarRemove,
    handleDepartureCalendarRemove,
    handleCasherWithRemove,
    handleDriverWithRemove,
    handleVehicleWithRemove,
    onChangeMainQueryCallback,
  ]);

  /**
   * Date time filling ...
   */

  // date range
  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const [{ startDate, endDate }, setDateRange] = useState({
    startDate: new Date().getTime(),
    endDate: new Date().getTime(),
  });

  const [{ month: departureMonth, year: departureYear }, setDepartureDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const [{ startDate: departureStartDate, endDate: departureEndDate }, setDepartureDateRange] = useState({
    startDate: new Date().getTime(),
    endDate: new Date().getTime(),
  });

  function handleMonthChange(month: number, year: number): any {
    setDate({ month, year });
  }

  function handleCalendarChange({ start, end }): void {
    const normalizedStartDate = new Date(start);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(end || start);
    normalizedEndDate.setHours(23, 59, 59, 999);

    const startDate = normalizedStartDate.valueOf();
    const endDate = normalizedEndDate.valueOf();

    setDateRange({ startDate: start.valueOf(), endDate: end.valueOf() }); // for calendar
    setDateRanges([normalizedStartDate.valueOf(), normalizedEndDate.valueOf()]); // for saving ...
    onChangeMainQueryCallback({ createdAt: `gte:${startDate},lte:${endDate}`, page: 1 });
  }

  function handleDepartureMonthChange(month: number, year: number): void {
    setDepartureDate({ month, year });
  }

  function handleDepartureCalendarChange({ start, end }): void {
    const normalizedStartDate = new Date(start);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(end || start);
    normalizedEndDate.setHours(23, 59, 59, 999);

    const startDate = normalizedStartDate.valueOf();
    const endDate = normalizedEndDate.valueOf();

    setDepartureDateRange({ startDate: start.valueOf(), endDate: end.valueOf() });
    setDepartureDateRanges([startDate, endDate]);
    onChangeMainQueryCallback({ 'order_data.departure_date': `gte:${startDate},lte:${endDate}`, page: 1 });
  }

  const filters = [
    {
      key: 'paymentMethod',
      label: 'Loại thanh toán',
      filter: (
        <Select
          value={paymentMethod}
          label="Loại thanh toán"
          labelInline
          onChange={handlePaymentTypeWithChange}
          options={[
            {
              label: 'Tất cả các loại',
              value: '',
            },
            {
              label: 'Chuyển khoản',
              value: 'BANK',
            },
            {
              label: 'Tiền mặt',
              value: 'CASH',
            },
            {
              label: 'Công nợ khách',
              value: 'DEBT_CUSTOMER',
            },
            {
              label: 'Công nợ đại lý',
              value: 'DEBT_AGENT',
            },
            {
              label: 'Công nợ bên thứ ba',
              value: 'THIRD_PARTY',
            },
          ]}
        />
      ),
      shortcut: true,
    },
    {
      key: 'casher',
      label: 'Người thu tiền',
      filter: (
        <QuickSearchUser
          title={''}
          noWrap
          placeholder="Tìm theo tên"
          user_role="driver,staff,sale,sale_admin,admin,super_admin"
          onClose={(u: TypedUser) => {
            handleCasherWithChange(u.user_id);
            setCasherName(u.display_name);
          }}
        />
      ),
      shortcut: true,
    },
    {
      key: 'driver',
      label: 'Lái xe',
      filter: (
        <QuickSearchUser
          title={''}
          noWrap
          placeholder="Tìm theo tên lái xe"
          user_role="driver"
          onClose={(u: TypedUser) => {
            handleDriverWithChange(u.user_id);
            setDriverName(u.display_name);
          }}
        />
      ),
      shortcut: true,
    },
    {
      key: 'vehicle',
      label: 'Xe',
      filter: (
        <QuickSearchVehicle
          title={''}
          placeholder="Tìm theo biển số xe"
          onSelect={(vehicle) => {
            if (!vehicle) return;
            handleVehicleWithChange(String(vehicle.id));
            setVehicleName(vehicle.license_plate);
          }}
        />
      ),
      shortcut: true,
    },
    {
      key: 'daterange',
      label: 'Ngày thanh toán',
      filter: (
        <>
          <DatePicker
            month={month}
            year={year}
            selected={{
              start: new Date(startDate),
              end: new Date(endDate),
            }}
            onMonthChange={handleMonthChange}
            onChange={handleCalendarChange}
            multiMonth={false}
            allowRange
          />
        </>
      ),
      shortcut: true,
    },
    {
      key: 'departureDateRange',
      label: 'Ngày đi',
      filter: (
        <DatePicker
          month={departureMonth}
          year={departureYear}
          selected={{
            start: new Date(departureStartDate),
            end: new Date(departureEndDate),
          }}
          onMonthChange={handleDepartureMonthChange}
          onChange={handleDepartureCalendarChange}
          multiMonth={false}
          allowRange
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters: IndexFiltersProps['appliedFilters'] = [];

  //   if (!helpers.isEmpty(paymentMethod)) {
  appliedFilters.push({
    key: 'paymentMethod',
    label: disambiguateLabel('paymentMethod', paymentMethod),
    onRemove: handlePaymentTypedWithRemove,
  });
  //   }

  // if (!helpers.isEmpty(userRole)) {
  appliedFilters.push({
    key: 'casher',
    label: disambiguateLabel('casher', casherID),
    onRemove: handleCasherWithRemove,
  });
  appliedFilters.push({
    key: 'driver',
    label: disambiguateLabel('driver', driverID),
    onRemove: handleDriverWithRemove,
  });
  appliedFilters.push({
    key: 'vehicle',
    label: disambiguateLabel('vehicle', vehicleID),
    onRemove: handleVehicleWithRemove,
  });
  // }

  // if (!helpers.isEmpty(dateRanges)) {
  // const key = 'daterange';
  appliedFilters.push({
    key: 'daterange',
    label: disambiguateLabel('daterange', dateRanges),
    onRemove: handCalendarRemove,
  });
  appliedFilters.push({
    key: 'departureDateRange',
    label: disambiguateLabel('departureDateRange', departureDateRanges),
    onRemove: handleDepartureCalendarRemove,
  });
  // }

  /**
   * Lưu lại ...
   * @param value
   * @returns
   */
  const onCreateNewView = async (value: string) => {
    setItemStrings([...itemStrings, value]);
    setSelected(itemStrings.length);
    updateItem(key_for_saving_setting + value, getCurrentSetting());
    return true;
  };
  const onHandleSave = useCallback(async () => {
    if (!itemStrings[selected]) return;
    updateItem(key_for_saving_setting + itemStrings[selected], getCurrentSetting());
    return true;
  }, [selected, itemStrings]);

  const primaryActionForFilterFields: IndexFiltersProps['primaryAction'] =
    selected === 0
      ? {
          type: 'save-as',
          onAction: onCreateNewView,
          disabled: false,
          loading: false,
        }
      : {
          type: 'save',
          onAction: onHandleSave,
          disabled: false,
          loading: false,
        };

  /***************************************************************************
  /***************************************************************************
  /***************************************************************************
   * FILTERs
   */

  return (
    <>
      <IndexFilters
        loading={loading || _loading || updating}
        sortOptions={sortOptions}
        sortSelected={sortSelected}
        queryValue={queryValue}
        queryPlaceholder="Tìm kiếm"
        onQueryChange={handleFiltersQueryChange}
        onQueryClear={handleQueryValueRemove}
        onSort={setSortSelected}
        primaryAction={primaryActionForFilterFields}
        cancelAction={{
          onAction: () => {},
          disabled: false,
          loading: false,
        }}
        tabs={tabs}
        selected={selected}
        onSelect={setSelected}
        canCreateNewView={true}
        onCreateNewView={onCreateNewView}
        onClearAll={handleFiltersClearAll}
        mode={mode}
        setMode={setMode}
        filters={filters}
        appliedFilters={appliedFilters}
      />
      {mode === 'DEFAULT' ? (
        <div className="Polaris-Filters__FiltersWrapper Polaris-Filters__FiltersWrapperWithAddButton filterWrapper">
          <div className="Polaris-Filters__FiltersInner">
            {!helpers.isEmpty(queryValue) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('keyword', queryValue)}
              </span>
            ) : null}

            {!helpers.isEmpty(casherID) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('casher', casherID)}
              </span>
            ) : null}

            {!helpers.isEmpty(driverID) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('driver', driverID)}
              </span>
            ) : null}

            {!helpers.isEmpty(vehicleID) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('vehicle', vehicleID)}
              </span>
            ) : null}

            {!helpers.isEmpty(paymentMethod) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('paymentMethod', paymentMethod)}
              </span>
            ) : null}

            {!helpers.isEmpty(dateRanges) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('daterange', dateRanges)}
              </span>
            ) : null}

            {!helpers.isEmpty(departureDateRanges) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('departureDateRange', departureDateRanges)}
              </span>
            ) : null}

            {helpers.isEmpty(queryValue) &&
            helpers.isEmpty(casherID) &&
            helpers.isEmpty(driverID) &&
            helpers.isEmpty(vehicleID) &&
            helpers.isEmpty(paymentMethod) &&
            helpers.isEmpty(dateRanges) &&
            helpers.isEmpty(departureDateRanges) ? (
              <InlineStack blockAlign="center" align="start" wrap={false}>
                <Link removeUnderline onClick={() => setMode('FILTERING' as any)}>
                  {'   '}{' '}
                  <Text as="span" variant="bodySm" tone="subdued">
                    Mở bộ lọc (hoặc bấm nút F)
                  </Text>
                </Link>
              </InlineStack>
            ) : (
              <Button variant="plain" size="micro" onClick={handleFiltersClearAll}>
                xóa hết
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

